import { FC, Fragment, useContext, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle, faPencil, faRobot, faTrash } from '@fortawesome/free-solid-svg-icons';
import {
  faMoneyCheckDollarPen,
  faTrashCanArrowTurnLeft,
} from '@fortawesome/sharp-duotone-solid-svg-icons';
import { EditProposalContext } from 'contexts/EditProposaContext';
import { formatPriceCurrency, getCurrency } from 'utils/utils';
import { formatPriceProposal } from 'utils/shared/utils';
import withErrorBoundary from '../../withErrorBoundary';
import { useTranslation } from 'react-i18next';
import Tooltip from 'components/ui/Tooltip/Tooltip';
import ProposalHotelDataModal, { ProposalHotelDataTab } from './ProposalHotelDataModal';
import { applyHotelDataDraftToProposalHotel, isHotelDataConfigOnlyPayload, TripBuilderHotelDataDraftPayload } from 'utils/shared/tripResultBuilderPreview';
import { applyClaudeHotelInfoConfigToExternalParam } from 'utils/shared/claudeProposalInformation';
import { applyHotelInfoConfigToExternalParam } from 'utils/shared/hotelProposalInformation';
import { MainContext } from 'contexts/MainContext';
import { gIco, trIco } from 'constants/generic';

interface ProposalEditHeadProps {
  proposalId: string;
  displayPrice: boolean;
}

const ProposalEditHead: FC<ProposalEditHeadProps> = ({ ...props }) => {
  const [t] = useTranslation();
  const {
    setProposal
  } = useContext(MainContext);
  const [showHotelDataPopup, setShowHotelDataPopup] = useState(false);
  const [hotelDataTab, setHotelDataTab] = useState<ProposalHotelDataTab>('google');
  const { editMode, onClickEdit, onClickDelete, onClickEditPrice, proposal } =
    useContext(EditProposalContext);
  const currentProposal = proposal?.listOfProducts?.find((p) => p.id === props.proposalId);
  const proposalIndex = proposal?.listOfProducts?.findIndex((p) => p.id === props.proposalId) || 0;
  const isHotelProposal = !!currentProposal?.listOfItems?.some((item) => item.proposal?.code === 'HTL');
  const currentHotel = useMemo(
    () => currentProposal?.listOfItems?.find((item) => item.proposal?.code === 'HTL')?.hotels?.[0],
    [currentProposal]
  );
  const hasAiHotelInfo = !!currentHotel?.aI_HotelInformatiom?.content?.length;
  const price = currentProposal
    ? formatPriceCurrency(currentProposal.salePrice, getCurrency(currentProposal.currency))
    : '';
  const originalPrice = currentProposal
    ? formatPriceCurrency(currentProposal.amount, getCurrency(currentProposal.currency))
    : '';
  const viewPrice =
    typeof currentProposal?.viewValue === 'number'
      ? formatPriceProposal(
        currentProposal.amount,
        currentProposal.currency,
        currentProposal.viewType,
        currentProposal.viewValue
      )
      : price;
  const onSaveHotelDataDraft = (payload: TripBuilderHotelDataDraftPayload) => {
    if (!editMode || !props.proposalId) {
      return;
    }

    setProposal((prev: any) => {
      if (!prev?.existingProposal) {
        return prev;
      }

      const existingProposal = JSON.parse(JSON.stringify(prev.existingProposal));
      const product = existingProposal.listOfProducts?.find((p: any) => p.id === props.proposalId);
      const hotel = product?.listOfItems
        ?.find((item: any) => item.proposal?.code === 'HTL')
        ?.hotels?.[0];

      if (!hotel) {
        return prev;
      }

      const isConfigOnly = isHotelDataConfigOnlyPayload(payload);

      if (!isConfigOnly) {
        const { serpHotel, tripAdvisorHotel } = applyHotelDataDraftToProposalHotel(hotel, payload);
        hotel.serpHotel = serpHotel || undefined;
        hotel.tripAdvisorHotel = tripAdvisorHotel || undefined;
        if (payload.sourceSerpHotel) {
          hotel.serpHotelEntire = payload.sourceSerpHotel;
        }
        if (payload.sourceTripadvisorHotel) {
          hotel.tripAdvisorHotelEntire = payload.sourceTripadvisorHotel;
        }
        product.edited = true;
      }

      if (payload.claudeHotelInfoConfig) {
        existingProposal.externalParam_1 = applyClaudeHotelInfoConfigToExternalParam(
          existingProposal.externalParam_1,
          hotel,
          payload.claudeHotelInfoConfig,
        );
        (existingProposal as any).externalParamEdited = true;
      }
      if (payload.hotelInfoConfig) {
        existingProposal.externalParam_1 = applyHotelInfoConfigToExternalParam(
          existingProposal.externalParam_1,
          hotel,
          payload.hotelInfoConfig,
        );
        (existingProposal as any).externalParamEdited = true;
      }

      return { ...prev, existingProposal };
    });
  };

  return (
    <Fragment>
      {props.displayPrice && !editMode && <div>{price}</div>}
      {editMode && !!currentProposal && (
        <span className="pr-edit-head">
          <div className="pr-edit-head-price cur-poi" onClick={() => onClickEditPrice(proposalIndex)}>
            {editMode && originalPrice !== viewPrice && (
              <span className="text-light">{originalPrice} / </span>
            )}
            {currentProposal?.displayPrice && <span>{viewPrice}</span>}
            <FontAwesomeIcon icon={faMoneyCheckDollarPen} color="var(--color-contrast)" />
          </div>

          {!currentProposal.removed && isHotelProposal && (
            <Fragment>
              <Tooltip content={t('hotel.info')}>
                <div
                  className="display-flex cur-poi pr-source-btn"
                  onClick={() => {
                    setHotelDataTab('hotel_info');
                    setShowHotelDataPopup(true);
                  }}
                >
                  <FontAwesomeIcon icon={faInfoCircle} className="pr-source-btn-icon" />
                </div>
              </Tooltip>

              {hasAiHotelInfo && (
                <Tooltip content={t('proposal.ai_hotel_info')}>
                  <div
                    className="display-flex cur-poi pr-source-btn"
                    onClick={() => {
                      setHotelDataTab('ai_hotel_info');
                      setShowHotelDataPopup(true);
                    }}
                  >
                    <FontAwesomeIcon icon={faRobot} className="pr-source-btn-icon" />
                  </div>
                </Tooltip>
              )}
              <Tooltip content={t('hotel.google')}>
                <div
                  className="display-flex cur-poi pr-source-btn"
                  onClick={() => {
                    setHotelDataTab('google');
                    setShowHotelDataPopup(true);
                  }}
                >
                  <img src={gIco} alt="g" className="pr-source-btn-img" />
                </div>
              </Tooltip>
              <Tooltip content={t('hotel.tripadvisor')}>
                <div
                  className="display-flex cur-poi pr-source-btn"
                  onClick={() => {
                    setHotelDataTab('tripadvisor');
                    setShowHotelDataPopup(true);
                  }}
                >
                  <img src={trIco} alt="t" className="pr-source-btn-img" />
                </div>
              </Tooltip>
            </Fragment>
          )}
          {!currentProposal.removed && (
            <Tooltip content={t('proposal.edit')}>
              <div className="display-flex cur-poi pr-source-btn" onClick={() => onClickEdit(proposalIndex)}>
                <FontAwesomeIcon icon={faPencil} className="pr-source-btn-icon" />
              </div>
            </Tooltip>
          )}

          <Tooltip
            content={
              currentProposal.removed
                ? t('proposal.restore', { defaultValue: 'Restore' })
                : t('proposal.remove_proposal')
            }
          >
            <div className="display-flex cur-poi pr-source-btn" onClick={() => onClickDelete(proposalIndex)}>
              <FontAwesomeIcon
                icon={currentProposal.removed ? faTrashCanArrowTurnLeft : faTrash}
                className="pr-source-btn-icon"
              />
            </div>
          </Tooltip>
        </span>
      )}
      <ProposalHotelDataModal
        show={showHotelDataPopup}
        onClose={() => setShowHotelDataPopup(false)}
        hotelDataTab={hotelDataTab}
        setHotelDataTab={setHotelDataTab}
        currentHotel={currentHotel}
        currency={currentProposal?.currency}
        externalParam_1={proposal?.externalParam_1}
        onSaveDraft={onSaveHotelDataDraft}
      />
    </Fragment>
  );
};

export default withErrorBoundary(ProposalEditHead, 'ProposalEditHead');
